import React from 'react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {Breakpoint} from '@mui/material';
import {CatastrophicErrorBoundary} from '@practicegenius/pg-ui/src/components/CatastrophicErrorBoundary';
import {AuthApiProvider} from '../http/AuthApiProvider';
import {UserProfileProvider} from './UserProfileProvider';
import logger from '../utils/logger';
import {
  AuthenticatedThemeProvider,
  UnauthenticatedThemeProvider,
} from '../shared/Material/theme';
import {CatastrophicErrorScreen} from './CatastrophicErrorScreen';
import {SnackbarProvider} from './SnackbarProvider';

/**
 *  The sharedQueryClient is used between all instances of StandardProvider components
 *    so the single ReactQueryDevtools instance can display all queries on the page between
 *    different mount points
 */
const sharedQueryClient = new QueryClient();

let renderedDevTools = false;
/** Who needs multiple instances of query devtools? Ew */
const SingletonReactQueryDevtools = React.memo(() => {
  if (renderedDevTools) return null;

  renderedDevTools = true;

  return <ReactQueryDevtools buttonPosition="bottom-left" />;
});

type StandardProviderProps = {
  breakpoint?: Breakpoint;
  /** Provide a name for the app using this provider. This is currently only used in the Catastrophic Error boundary.
   *  Default: 'sites-dot'
   */
  appName?: string;
  showErrorMessage?: boolean;
};

const StandardCatastrophicErrorBoundary: React.FCC<{
  appName?: string;
  showError: boolean;
}> = ({children, appName = 'sites-dot', showError}) => (
  <CatastrophicErrorBoundary
    errorLogMessage={`Error rendering in ${appName && 'sites-dot'}`}
    logger={logger}
    errorNode={showError ? <CatastrophicErrorScreen /> : null}
  >
    {children}
  </CatastrophicErrorBoundary>
);

const SharedProvider: React.FCC<Omit<StandardProviderProps, 'breakpoint'>> = ({
  children,
  appName,
  showErrorMessage = false,
}) => (
  <SnackbarProvider>
    <StandardCatastrophicErrorBoundary
      appName={appName}
      showError={showErrorMessage}
    >
      <QueryClientProvider client={sharedQueryClient}>
        <SingletonReactQueryDevtools />
        {children}
      </QueryClientProvider>
    </StandardCatastrophicErrorBoundary>
  </SnackbarProvider>
);

/** Provides Thene, Snackbars, Standard Error Boundary, and React Query Client */
export const UnauthenticatedStandardProvider: React.FCC<StandardProviderProps> =
  ({breakpoint, appName, children, showErrorMessage = false}) => {
    return (
      <UnauthenticatedThemeProvider breakpoint={breakpoint}>
        <SharedProvider appName={appName} showErrorMessage={showErrorMessage}>
          {children}
        </SharedProvider>
      </UnauthenticatedThemeProvider>
    );
  };

export const withUnauthenticatedStandardProvider =
  (providerProps: StandardProviderProps = {}) =>
  (Inner: React.ComponentType<any>) =>
  (props: any) =>
    (
      <UnauthenticatedStandardProvider {...providerProps}>
        <Inner {...props} />
      </UnauthenticatedStandardProvider>
    );

/** Provides everything from the unauth provider + authenticated API and logged-in user data */
export const StandardProvider: React.FCC<StandardProviderProps> = ({
  breakpoint,
  appName,
  children,
  showErrorMessage = false,
}) => (
  <SharedProvider appName={appName} showErrorMessage={showErrorMessage}>
    <AuthApiProvider>
      <UserProfileProvider showErrorMessage={showErrorMessage}>
        <AuthenticatedThemeProvider breakpoint={breakpoint}>
          {children}
        </AuthenticatedThemeProvider>
      </UserProfileProvider>
    </AuthApiProvider>
  </SharedProvider>
);

export const withStandardProvider =
  (providerProps: StandardProviderProps = {}) =>
  (Inner: React.ComponentType<any>) =>
  (props: any) =>
    (
      <StandardProvider {...providerProps}>
        <Inner {...props} />
      </StandardProvider>
    );
